import { forwardRef, useState } from 'react';

import { Button, CircularProgress, Link, Typography } from '@mui/material';
import { CheckIcon, ChevronDownDashboardIcon as ChevronDownIcon, colors, RadioUncheckedIcon } from '@truvhq/ui';
import { clsx } from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

import type { ChecklistStep, StepStatus } from 'entities/onboarding/types';
import { toInternalPath } from 'features/onboarding/lib/links';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { toYouTubeEmbedUrl } from 'features/onboarding/lib/video';

import PlayIcon from './icons/play.svg?react';

/** The design's collapse curve, shared with the pane so header and rows open on one motion. */
export const COLLAPSE_TRANSITION = { duration: 0.24, ease: [0.22, 0.7, 0.3, 1] } as const;

type ChecklistStepRowProps = {
  step: ChecklistStep;
  blockerTitle: string | null;
  isExpanded: boolean;
  onExpand: () => void;
  onOpenBlocker: () => void;
  onSettle: (status: StepStatus) => Promise<unknown>;
  busy: StepStatus | null;
};

type StepActionsProps = {
  step: ChecklistStep;
  blockerTitle?: string | null;
  onOpenBlocker?: () => void;
  busy: StepStatus | null;
  onSettle: (status: StepStatus) => Promise<unknown>;
};

function StepMarker({ status }: { status: ChecklistStep['status'] }) {
  if (status === 'complete') {
    return (
      <div
        role="img"
        aria-label="Completed"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success text-white"
      >
        <CheckIcon width={12} height={12} aria-hidden />
      </div>
    );
  }
  return (
    <RadioUncheckedIcon
      role="img"
      aria-label={status === 'skipped' ? 'Skipped' : 'Not done'}
      className={clsx('h-5 w-5 shrink-0', status === 'skipped' ? 'text-grey30' : 'text-grey40')}
    />
  );
}

// Routed client-side when the step's page lives inside the dashboard, so following it doesn't spawn
// a second copy of the app.
function StepAction({ link, title, isBlocked }: { link: string; title: string; isBlocked: boolean }) {
  const closePanel = useOnboardingDialogStore((state) => state.close);
  const internalPath = toInternalPath(link);
  const sx = {
    borderRadius: 999,
    textTransform: 'none',
    px: 1.75,
    py: 0.625,
    fontSize: '0.875rem',
    boxShadow: 'none',
    '&.Mui-disabled': {
      backgroundColor: colors.primary.bright,
      color: 'rgba(255, 255, 255, 0.9)',
      cursor: 'not-allowed',
    },
  } as const;
  // Only one step is expanded at a time, so "Open" reads unambiguously on screen; the label names
  // the step for anyone who can't see which card it sits in.
  const label = `Open ${title}`;

  if (isBlocked) {
    return (
      <Button variant="contained" disabled aria-label={label} sx={sx}>
        Open
      </Button>
    );
  }
  if (!internalPath) {
    // Opens in its own tab, so the panel is not in the way of anything and stays as it was.
    return (
      <Button variant="contained" href={link} target="_blank" rel="noopener noreferrer" aria-label={label} sx={sx}>
        Open
      </Button>
    );
  }
  // The step's page is what the user is being sent to look at, and the panel covers it.
  return (
    <Button
      variant="contained"
      component={RouterLink}
      to={internalPath}
      onClick={closePanel}
      aria-label={label}
      sx={sx}
    >
      Open
    </Button>
  );
}

/** Why a step can't be started yet, and the way to the step that unblocks it. */
export function StepGateNote({ blockerTitle, onOpenBlocker }: { blockerTitle: string; onOpenBlocker: () => void }) {
  return (
    <div className="mt-1.5 w-fit rounded-lg bg-grey20 px-[11px] py-2 text-grey60 text-xs leading-[17px]">
      Available after you{' '}
      <Link component="button" type="button" onClick={onOpenBlocker} className="font-medium text-xs">
        {blockerTitle.toLowerCase()}
      </Link>
    </div>
  );
}

// MUI gives a text button its own horizontal padding, which reads as an indent when the button is
// the first thing in the row. Pull the label back level with the title and description above it.
const TEXT_ACTION_PADDING_X = 0.75;
const textActionSx = (isFirst: boolean) =>
  ({
    px: TEXT_ACTION_PADDING_X,
    py: 0.875,
    ml: isFirst ? -TEXT_ACTION_PADDING_X : 0,
    fontSize: '0.875rem',
    textTransform: 'none',
  }) as const;

/**
 * The step's own page, then the quieter way to tick it off by hand — or to take that back.
 *
 * A lock blocks the page — there is nothing to do there yet — but not the tick: someone who did the
 * work another way can still record it. A step the backend verifies for itself is the exception, and
 * says so with `manual_complete`: there is no other way to buy a subscription, so offering to mark
 * it done only invites a checklist that disagrees with the account.
 */
export function ChecklistStepActions({ step, blockerTitle, onOpenBlocker, busy, onSettle }: StepActionsProps) {
  const isSettled = step.status === 'complete' || step.status === 'skipped';

  if (isSettled) {
    return (
      <>
        {/* Company-level steps settle for everyone, so a teammate's action would otherwise look like
            the step ticked itself off. */}
        {step.completed_by && (
          <Typography variant="body2" className="mt-2 text-grey60 text-xs">
            {step.status === 'skipped' ? 'Skipped' : 'Completed'} by {step.completed_by}
          </Typography>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button variant="text" sx={textActionSx(true)} disabled={busy !== null} onClick={() => onSettle('pending')}>
            {busy === 'pending' ? <CircularProgress size={14} color="inherit" /> : 'Undo'}
          </Button>
        </div>
      </>
    );
  }

  const canMarkDone = step.manual_complete !== false;

  return (
    <>
      {step.locked && blockerTitle && onOpenBlocker && (
        <StepGateNote blockerTitle={blockerTitle} onOpenBlocker={onOpenBlocker} />
      )}
      {(step.link || canMarkDone) && (
        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          {step.link && <StepAction link={step.link} title={step.title} isBlocked={Boolean(step.locked)} />}
          {canMarkDone && (
            <Button
              variant="text"
              sx={textActionSx(!step.link)}
              disabled={busy !== null}
              onClick={() => onSettle('complete')}
            >
              {busy === 'complete' ? <CircularProgress size={14} color="inherit" /> : 'Mark done'}
            </Button>
          )}
        </div>
      )}
    </>
  );
}

/**
 * The step's walkthrough, behind a cover of our own.
 *
 * YouTube's default poster is a frame of the video with its own chrome over it, which looks nothing
 * like the rest of the panel. Playing on demand also means a step nobody opens never loads YouTube.
 */
function StepVideo({ embedUrl, title }: { embedUrl: string; title: string }) {
  const [isPlaying, setIsPlaying] = useState(false);

  if (isPlaying) {
    return (
      <div className="relative mt-3 aspect-video w-full overflow-hidden rounded-lg">
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`${embedUrl}?autoplay=1`}
          title={`${title} walkthrough`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsPlaying(true)}
      // Deliberately not naming the step: the card already does, and repeating it here gives the
      // card two controls whose accessible names both contain the title.
      aria-label="Play walkthrough"
      className="relative mt-3 flex aspect-video w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border-0 bg-blue p-0"
    >
      <img src="/favicon.svg" alt="" aria-hidden className="absolute top-3 left-3 h-6 w-6" />
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-blue">
        <PlayIcon width={22} height={22} aria-hidden />
      </span>
    </button>
  );
}

export const ChecklistStepRow = forwardRef<HTMLLIElement, ChecklistStepRowProps>(function ChecklistStepRow(
  { step, blockerTitle, isExpanded, onExpand, onOpenBlocker, onSettle, busy },
  ref,
) {
  const isSettled = step.status === 'complete' || step.status === 'skipped';
  const embedUrl = toYouTubeEmbedUrl(step.video_url);
  const prefersReducedMotion = useReducedMotion();

  return (
    <li
      ref={ref}
      className={clsx('rounded-xl border bg-white', isExpanded ? 'border-primary-bright' : 'border-grey30')}
    >
      {/* The card's padding lives on the button, so every part of a collapsed row opens it. */}
      <button
        type="button"
        onClick={onExpand}
        aria-expanded={isExpanded}
        className="flex w-full cursor-pointer items-start gap-3 bg-transparent px-3 py-2.5 text-left"
      >
        <StepMarker status={step.status} />
        <Typography className={clsx('min-w-0 flex-1 text-sm', isSettled ? 'text-grey60' : 'font-medium text-grey90')}>
          {step.title}
        </Typography>
        <motion.span
          className="mt-0.5 flex shrink-0"
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : COLLAPSE_TRANSITION}
          aria-hidden
        >
          <ChevronDownIcon className="h-5 w-5 text-grey60" />
        </motion.span>
      </button>

      {/* Nothing inside moves — the card just reveals what is already laid out. Hidden once closed,
          so the controls in here leave the tab order. */}
      <motion.div
        className="overflow-hidden"
        initial={false}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={prefersReducedMotion ? { duration: 0 } : COLLAPSE_TRANSITION}
        aria-hidden={!isExpanded}
      >
        <div className="px-3 pb-2.5 pl-11">
          {step.description && (
            <Typography variant="body2" className="text-grey70">
              {step.description}
            </Typography>
          )}
          {embedUrl && <StepVideo embedUrl={embedUrl} title={step.title} />}
          <ChecklistStepActions
            step={step}
            blockerTitle={blockerTitle}
            onOpenBlocker={onOpenBlocker}
            busy={busy}
            onSettle={onSettle}
          />
        </div>
      </motion.div>
    </li>
  );
});
